import { type FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { AppNav, storeTrail } from "../../components/AppNav";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Layout } from "../../components/Layout";
import { Select } from "../../components/Select";
import { ApiError, api } from "../../lib/api";
import type { NvrResponse, StoreResponse, UpdateNvrRequest } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { NVR_BRANDS } from "../../lib/nvrBrands";
import styles from "./NvrView.module.css";

type NvrForm = {
  name: string;
  host: string;
  rtsp_port: string;
  http_port: string;
  brand: string;
  model: string;
  serial_number: string;
  channel_count: string;
};

const nvrToForm = (nvr: NvrResponse): NvrForm => ({
  name: nvr.name,
  host: nvr.host ?? "",
  rtsp_port: nvr.rtsp_port === null ? "" : String(nvr.rtsp_port),
  http_port: nvr.http_port === null ? "" : String(nvr.http_port),
  brand: nvr.brand ?? "",
  model: nvr.model ?? "",
  serial_number: nvr.serial_number ?? "",
  channel_count: nvr.channel_count === null ? "" : String(nvr.channel_count),
});

const parseNullableInt = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const nvrDiff = (form: NvrForm, nvr: NvrResponse): UpdateNvrRequest => {
  const patch: UpdateNvrRequest = {};
  const name = form.name.trim();
  if (name && name !== nvr.name) patch.name = name;
  const host = form.host.trim();
  if (host !== (nvr.host ?? "")) patch.host = host === "" ? null : host;
  const rtsp = parseNullableInt(form.rtsp_port);
  if (rtsp !== nvr.rtsp_port) patch.rtsp_port = rtsp;
  const http = parseNullableInt(form.http_port);
  if (http !== nvr.http_port) patch.http_port = http;
  const brand = form.brand.trim();
  if (brand !== (nvr.brand ?? "")) patch.brand = brand === "" ? null : brand;
  const model = form.model.trim();
  if (model !== (nvr.model ?? "")) patch.model = model === "" ? null : model;
  const serial = form.serial_number.trim();
  if (serial !== (nvr.serial_number ?? "")) patch.serial_number = serial === "" ? null : serial;
  const channelCount = parseNullableInt(form.channel_count);
  if (channelCount !== nvr.channel_count) patch.channel_count = channelCount;
  return patch;
};

export const NvrView = () => {
  const { user, logout } = useAuth();
  const { nvrId } = useParams<{ nvrId: string }>();

  const [nvr, setNvr] = useState<NvrResponse | null>(null);
  const [store, setStore] = useState<StoreResponse | null>(null);
  const [form, setForm] = useState<NvrForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!nvrId) return;
    let cancelled = false;
    (async () => {
      try {
        const loaded = await api.getNvr(nvrId);
        if (cancelled) return;
        setNvr(loaded);
        setForm(nvrToForm(loaded));
        const loadedStore = await api.getStore(loaded.store_id);
        if (cancelled) return;
        setStore(loadedStore);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load this NVR.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nvrId]);

  if (!nvrId) return <Navigate to="/stores" replace />;

  const update = (key: keyof NvrForm, value: string): void =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!nvr || !form) return;
    const patch = nvrDiff(form, nvr);
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateNvr(nvr.id, patch);
      setNvr(updated);
      setForm(nvrToForm(updated));
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't save this NVR.");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = (): void => {
    if (nvr) setForm(nvrToForm(nvr));
  };

  const dirty = nvr && form ? Object.keys(nvrDiff(form, nvr)).length > 0 : false;

  return (
    <Layout
      nav={
        <AppNav
          trail={storeTrail(store, nvr?.name ?? (loading ? "Loading" : "NVR"))}
          onSignOut={logout}
          userName={user?.display_name ?? user?.email}
          userAvatar={user?.avatar ?? null}
        />
      }
    >
      <main className={styles.content}>
        <header className={styles.header}>
          <Link to={store ? `/stores/${store.id}?tab=nvrs` : "/stores"} className={styles.crumb}>
            ← {store?.name ?? "Back"}
          </Link>
          <div className={styles.titleBlock}>
            <span className={styles.kicker}>NVR</span>
            <h1 className={styles.title}>
              {nvr?.name ?? (loading ? "Loading…" : "NVR not found")}
            </h1>
            {nvr && (
              <p className={styles.subtitle}>
                {[nvr.brand, nvr.model, nvr.host].filter(Boolean).join(" · ") || "Metadata only"}
              </p>
            )}
          </div>
        </header>

        {loading ? (
          <div className={styles.status}>Loading…</div>
        ) : !nvr || !form ? (
          <div className={styles.status}>{error ?? "NVR not found."}</div>
        ) : (
          <form className={styles.form} onSubmit={handleSave} noValidate>
            <div className={styles.formHeader}>
              <div>
                <span className={styles.kicker}>Recorder details</span>
                <p className={styles.subtitle}>Editable until recorder discovery is available.</p>
              </div>
              <div className={styles.actions}>
                <Button variant="outline" onClick={handleDiscard} disabled={!dirty || saving}>
                  Discard
                </Button>
                <Button type="submit" disabled={!dirty || saving}>
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>

            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-name">
                  Nickname
                </label>
                <Input
                  id="nvr-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  disabled={saving}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-host">
                  Host / IP
                </label>
                <Input
                  id="nvr-host"
                  value={form.host}
                  onChange={(e) => update("host", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div className={styles.grid3}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-rtsp">
                  RTSP port
                </label>
                <Input
                  id="nvr-rtsp"
                  type="number"
                  min={1}
                  max={65535}
                  value={form.rtsp_port}
                  onChange={(e) => update("rtsp_port", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-http">
                  HTTP port
                </label>
                <Input
                  id="nvr-http"
                  type="number"
                  min={1}
                  max={65535}
                  value={form.http_port}
                  onChange={(e) => update("http_port", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-channels">
                  Channels
                </label>
                <Input
                  id="nvr-channels"
                  type="number"
                  min={0}
                  value={form.channel_count}
                  onChange={(e) => update("channel_count", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div className={styles.grid3}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-brand">
                  Brand
                </label>
                <Select
                  id="nvr-brand"
                  value={form.brand}
                  onChange={(e) => update("brand", e.target.value)}
                  disabled={saving}
                >
                  <option value="">—</option>
                  {(form.brand && !(NVR_BRANDS as readonly string[]).includes(form.brand)
                    ? [form.brand, ...NVR_BRANDS]
                    : NVR_BRANDS
                  ).map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-model">
                  Model
                </label>
                <Input
                  id="nvr-model"
                  value={form.model}
                  onChange={(e) => update("model", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="nvr-serial">
                  Serial number
                </label>
                <Input
                  id="nvr-serial"
                  value={form.serial_number}
                  onChange={(e) => update("serial_number", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            {error && (
              <div role="alert" className={styles.error}>
                {error}
              </div>
            )}
          </form>
        )}
      </main>
    </Layout>
  );
};
